import { useMemo } from 'react';
import { ChartMode } from './types';
import { AnalysisStatus, QueryType } from '../../utils/apollo/graphqlSchema';
import ChartControlBar from './ChartControlBar';
import ChartWrapper from './ChartWrapper';
import NextSteps from './NextSteps';
import ChartViewController from './ChartViewController';
import ChartAnalysisTable from './ChartAnalysisTable';
import useCompareChartSettings from '../../utils/compareChartSettings';
import { useNavigationBlocker } from '../../utils/hooks';
import { useChartContext } from './ChartContext';
import useCachedEntitiesSelector from '../../redux/selectorHooks/useCachedEntitiesSelector';
import { isNil } from 'lodash';
import { ChartViewOptions } from '../../redux/utils/chartSettings';
import { assertCondition } from '../../utils/types';
import { IS_SCREENSHOT_MODE } from '../../config/constants';
import styled from 'styled-components';
import CentralChartSetting from './CentralChartSetting';
import { useCentralChartSettingContext } from './CentralChartSetting/contexts/CentralChartSettingContext';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const ContentWrapper = styled.div<{ $isBuildMode: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  flex: 1;

  padding: ${({ $isBuildMode }) =>
    // Build Mode need to display the Central Chart Setting which has a different layout and column-gap
    // therefore we need to add padding to the chart wrapper
    $isBuildMode ? '12px 16px 12px 16px' : '0px'};
`;

const ChartWithData = () => {
  const chartContext = useChartContext();
  assertCondition(chartContext.__typename === 'BaseChartContext');
  const { isCentralChartSettingOpen } = useCentralChartSettingContext();
  const {
    metadata: { analysisId, mode, status, queryType, query },
    permissions: { entityPermissions },
    settings: { chartSettings },
  } = chartContext;

  const chartView = chartSettings.tableSettings.chartView;

  const hasUnsavedChanges = useCompareChartSettings(analysisId);
  const replaceReport = useCachedEntitiesSelector('replaceReport');
  const canReplaceReport = !isNil(replaceReport);

  const shouldShowNavigationBlocker = useMemo(() => {
    if (status !== AnalysisStatus.Success) {
      return false;
    }

    if (!entityPermissions.editAnalysis) {
      return false;
    }

    return canReplaceReport && hasUnsavedChanges;
  }, [
    status,
    entityPermissions.editAnalysis,
    canReplaceReport,
    hasUnsavedChanges,
  ]);

  // noinspection PointlessBooleanExpressionJS
  useNavigationBlocker(
    analysisId,
    // disabled until we figure out how to fix table widths changing chart settings
    false && shouldShowNavigationBlocker,
    'You have unsaved changes. Are you sure you want to leave?',
  );

  const isBuildMode = mode === ChartMode.Build;

  const isChartVisible =
    queryType === QueryType.Expression
      ? chartView !== ChartViewOptions.Table
      : true;
  const isChartViewControllerVisible =
    !IS_SCREENSHOT_MODE && queryType === QueryType.Expression && isBuildMode;
  const isTableVisible =
    queryType === QueryType.Expression &&
    (chartView === ChartViewOptions.Table ||
      (chartView === ChartViewOptions.ChartAndTable && isBuildMode));
  const showNextSteps =
    !IS_SCREENSHOT_MODE &&
    isBuildMode &&
    queryType !== QueryType.CohortSample &&
    queryType !== QueryType.Prediction &&
    queryType !== QueryType.Significance &&
    !query.schema.disabled;

  return (
    <Container>
      <ContentWrapper $isBuildMode={isBuildMode}>
        <ChartControlBar />
        {isChartVisible && <ChartWrapper analysisId={analysisId} mode={mode} />}

        {showNextSteps && <NextSteps />}
        {isChartViewControllerVisible && (
          <ChartViewController value={chartView} analysisId={analysisId} />
        )}
        {isTableVisible && (
          <ChartAnalysisTable analysisId={analysisId} mode={mode} />
        )}
      </ContentWrapper>

      {isCentralChartSettingOpen && mode === ChartMode.Build && (
        <CentralChartSetting />
      )}
    </Container>
  );
};

export default ChartWithData;
